import { Search, X, ArrowRight, Heart, ShoppingBag, Check } from 'lucide-react';
import {
  DrawerTrigger,
  DrawerClose,
  VerticalDrawer,
  VerticalDrawerContent,
} from '@socialflows/shared-lib';
import { Skeleton } from '@/components/ui/skeleton';
import { useState, useEffect } from 'react';
import { useProducts } from '@socialflows/shared-lib';
import { Product } from '@/entities/product.entity';
import { Category } from '@/entities/category.entity';
import { useWishlist } from '@/contexts/wishlist-context';
import { useCart } from '@/contexts/cart-context';
import { cn, resolveImageUrl } from '@/lib/utils';

import { Link } from 'react-router-dom';
import { QuickAddDialog } from '@/components/quick-add-dialog';

interface SearchDrawerProps {
  categories?: Category[];
}

export const SearchDrawer = ({ categories = [] }: SearchDrawerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [addedProducts, setAddedProducts] = useState<Set<string>>(new Set());
  const [quickAddProduct, setQuickAddProduct] = useState<Product | null>(null);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();

  const hasFilters = debouncedSearch.trim() || selectedCategories.length > 0;

  const { data: rawResults = [], isLoading: rawLoading } = useProducts({
    search: debouncedSearch || undefined,
    categoryId: selectedCategories.length === 1 ? selectedCategories[0].id : undefined,
    limit: 8,
  });

  const searchResults = (hasFilters ? rawResults : []) as unknown as Product[];
  const isLoading = hasFilters && rawLoading;

  const handleAddToCart = (product: Product) => {
    const hasVariants = product.variants && product.variants.length > 0;

    if (hasVariants) {
      // Open quick add dialog for products with options
      setQuickAddProduct(product);
      setIsQuickAddOpen(true);
    } else {
      // Add directly for products without options
      addToCart(product, 1);
      markAsAdded(product.id);
    }
  };

  const handleQuickAddConfirm = (
    product: Product,
    quantity: number,
    selectedVariants?: Record<string, string>
  ) => {
    addToCart(product, quantity, selectedVariants);
    markAsAdded(product.id);
    setIsQuickAddOpen(false);
  };

  const markAsAdded = (productId: string) => {
    setAddedProducts((prev) => new Set(prev).add(productId));
    // Reset after animation
    setTimeout(() => {
      setAddedProducts((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }, 2000);
  };

  const toggleCategory = (category: Category) => {
    setSelectedCategories((prev) => {
      const isSelected = prev.some((c) => c.id === category.id);
      if (isSelected) {
        return prev.filter((c) => c.id !== category.id);
      }
      return [...prev, category];
    });
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Reset search when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setDebouncedSearch('');
      setSelectedCategories([]);
      setAddedProducts(new Set());
      setQuickAddProduct(null);
      setIsQuickAddOpen(false);
    }
  }, [isOpen]);

  return (
    <VerticalDrawer onOpenChange={setIsOpen}>
      <DrawerTrigger asChild>
        <button
          type="button"
          className="p-2 text-foreground/80 hover:text-foreground transition-colors duration-200"
        >
          <Search className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </DrawerTrigger>

      <VerticalDrawerContent className="flex flex-col h-full">
        {/* Search Header */}
        <div className="px-6 py-4 border-b">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
                strokeWidth={1.5}
              />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                className="w-full h-12 pl-8 pr-4 text-lg bg-transparent border-none focus:outline-none placeholder:text-muted-foreground"
              />
            </div>
            <DrawerClose className="p-2 text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-5 w-5" strokeWidth={1.5} />
            </DrawerClose>
          </div>
        </div>

        {/* Category Filters */}
        {categories.length > 0 && (
          <div className="px-6 py-4 border-b">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
              Filter by Category
            </p>
            <div className="flex flex-wrap gap-2">
              {categories.slice(0, 8).map((category) => {
                const isSelected = selectedCategories.some((c) => c.id === category.id);
                return (
                  <button
                    key={category.id}
                    onClick={() => toggleCategory(category)}
                    className={cn(
                      'px-3 py-1.5 text-sm border transition-colors',
                      isSelected
                        ? 'bg-foreground text-background border-foreground'
                        : 'border-border hover:border-foreground text-foreground'
                    )}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-3 w-24" />
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-2">
                    <Skeleton className="w-16 h-16 rounded flex-shrink-0" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <div className="flex items-center gap-1">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <Skeleton className="w-8 h-8 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isLoading &&
            (searchQuery || selectedCategories.length > 0) &&
            searchResults.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchQuery
                    ? `No results found for "${searchQuery}"${selectedCategories.length > 0 ? ` in ${selectedCategories.map((c) => c.name).join(', ')}` : ''}`
                    : `No products found in ${selectedCategories.map((c) => c.name).join(', ')}`}
                </p>
              </div>
            )}

          {!isLoading && !searchQuery && selectedCategories.length === 0 && (
            <div className="text-center py-12">
              <Search className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" strokeWidth={1} />
              <p className="text-muted-foreground">Start typing or select a category to search</p>
            </div>
          )}

          {!isLoading && searchResults.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Products ({searchResults.length})
              </p>
              <div className="space-y-2">
                {searchResults.map((product) => {
                  const isWishlisted = isInWishlist(product.id);
                  return (
                    <div
                      key={product.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group"
                    >
                      {/* Product Image */}
                      <DrawerClose asChild>
                        <Link
                          to={`/products/${product.id}`}
                          className="relative w-16 h-16 flex-shrink-0 bg-muted/30 overflow-hidden rounded"
                        >
                          {product.media?.[0]?.url && (
                            <img
                              src={resolveImageUrl(product.media[0].url)}
                              alt={product.label}
                              className="absolute inset-0 object-cover"
                            />
                          )}
                        </Link>
                      </DrawerClose>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <DrawerClose asChild>
                          <Link to={`/products/${product.id}`}>
                            <h3 className="text-sm font-medium line-clamp-1 hover:underline">
                              {product.label}
                            </h3>
                          </Link>
                        </DrawerClose>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-sm text-foreground">
                            {product.price.toFixed(2)} TND
                          </span>
                          {product.compareAtPrice && product.compareAtPrice > product.price && (
                            <span className="text-xs text-muted-foreground line-through">
                              {product.compareAtPrice.toFixed(2)} TND
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleWishlist(product)}
                          className={cn(
                            'p-2 rounded-full transition-colors',
                            isWishlisted
                              ? 'text-red-500'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                        >
                          <Heart
                            className={cn('h-4 w-4', isWishlisted && 'fill-red-500')}
                            strokeWidth={1.5}
                          />
                        </button>
                        <button
                          onClick={() => handleAddToCart(product)}
                          disabled={addedProducts.has(product.id)}
                          className={cn(
                            'p-2 rounded-full transition-all duration-300',
                            addedProducts.has(product.id)
                              ? 'text-green-600 bg-green-100'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                          aria-label={
                            addedProducts.has(product.id) ? 'Added to cart' : 'Add to cart'
                          }
                        >
                          {addedProducts.has(product.id) ? (
                            <Check
                              className="h-4 w-4 animate-in zoom-in-50 duration-300"
                              strokeWidth={2}
                            />
                          ) : (
                            <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* View All Results */}
        {(searchQuery || selectedCategories.length > 0) && searchResults.length > 0 && (
          <div className="px-6 py-4 border-t">
            <DrawerClose asChild>
              <Link
                to={(() => {
                  const params = new URLSearchParams();
                  if (searchQuery) params.set('q', searchQuery);
                  if (selectedCategories.length > 0) {
                    params.set('categories', selectedCategories.map((c) => c.id).join(','));
                  }
                  return `/products/search?${params.toString()}`;
                })()}
                className="flex items-center justify-center gap-2 w-full h-12 bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
              >
                View all results
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </DrawerClose>
          </div>
        )}
      </VerticalDrawerContent>

      {/* Quick Add Dialog for variant selection */}
      <QuickAddDialog
        product={quickAddProduct}
        open={isQuickAddOpen}
        onClose={() => {
          setIsQuickAddOpen(false);
          setTimeout(() => setQuickAddProduct(null), 300);
        }}
        onAddToCart={handleQuickAddConfirm}
      />
    </VerticalDrawer>
  );
};
